import React, { useEffect, useState } from "react";
import { Button, Card, Badge, Form, Alert, Spinner as BSSpinner, Modal, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar as solidStar } from "@fortawesome/free-solid-svg-icons";
import { faStar as regularStar } from "@fortawesome/free-regular-svg-icons";
import { faEnvelope, faCheck, faTimes, faSync, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "../../components/spinner/Spinner";
import "./index.css";
import { useSelector, useDispatch } from "react-redux";
import { useFeatureAccess } from "../../hooks/useFeatureAccess";
import {
  addPageFavorite,
  removePageFavorite,
  fetchPageFavorite,
} from "../../store/actions/users/users.actions";
import {
  getGmailConnectionStatus,
  connectGmail,
  disconnectGmail,
  scanGmailInbox,
  checkScanStatus,
  checkImportStatus,
  listStagedITBs,
  importITBs,
  rejectITB,
  selectITB,
  selectAllITBs,
  deselectAllITBs,
  setITBFilter,
  clearITBMessages,
  setITBSuccessMessage,
  setITBErrorMessage,
} from "../../store/actions/itb/itb.actions";

const ITBImport = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const path = window.location.pathname.slice(1);

  // Redux state
  const itbState = useSelector((state) => state.itb);
  const {
    gmailStatus,
    loadingStatus,
    scanning,
    scanStatus,
    stagedITBs,
    loadingITBs,
    selectedITBs,
    importing,
    importStatus,
    filter,
    counts,
    successMessage,
    errorMessage,
  } = itbState;

  const { user: userDetails } = useSelector((state) => state.userDetails);
  const free_itbs = userDetails?.free_itbs;
  const { hasFeatureAccess, enforceAccess } = useFeatureAccess("ITB Import");
  const { isFavorite } = useSelector((state) => state.fetchPageFavorite);
  const { user } = useSelector((state) => state.auth);
  
  const [showImportModal, setShowImportModal] = useState(false);

  // Handle Gmail connection
  const handleConnectGmail = () => {
    dispatch(connectGmail());
  };

  // Handle Gmail disconnection
  const handleDisconnectGmail = () => {
    dispatch(disconnectGmail());
  };

  // Scan Gmail inbox for ITBs
  const handleScanInbox = () => {
    if (!hasFeatureAccess && (!free_itbs || free_itbs <= 0)) {
      enforceAccess();
      return;
    }
    dispatch(scanGmailInbox(50));
  };

  // Handle ITB selection
  const handleSelectITB = (itbId) => {
    dispatch(selectITB(itbId));
  };

  // Handle select all
  const handleSelectAll = () => {
    const pendingITBs = stagedITBs.filter((itb) => itb.status === "pending");
    if (selectedITBs.length === pendingITBs.length) {
      dispatch(deselectAllITBs());
    } else {
      dispatch(selectAllITBs());
    }
  };

  const handleOpenImportModal = () => {
    if (!hasFeatureAccess && (!free_itbs || free_itbs <= 0)) {
      enforceAccess();
      return;
    }
    setShowImportModal(true);
  };

  // Import selected ITBs
  const handleImportITBs = async () => {
    if (selectedITBs.length === 0) {
      return;
    }

    // For users without ITB Import feature, enforce the remaining free imports limit.
    if (!hasFeatureAccess) {
      if (!free_itbs || free_itbs <= 0) {
        setShowImportModal(false);
        navigate("/pricing");
        return;
      }

      if (selectedITBs.length > free_itbs) {
        dispatch(
          setITBErrorMessage(
            `You can import up to ${free_itbs || 0} ITBs with your remaining free imports. Please select fewer ITBs or purchase plan.`
          )
        );
        setShowImportModal(false);
        return;
      }
    }

    setShowImportModal(false);
    dispatch(importITBs(selectedITBs));
    
    // Note: Navigation will be handled after import completes (see useEffect below)
  };

  // Reject ITB
  const handleRejectITB = (itbId) => {
    dispatch(rejectITB(itbId));
  };

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    dispatch(setITBFilter(newFilter));
    dispatch(listStagedITBs(newFilter));
  };

  const handleFavoriteToggle = () => {
    if (isFavorite) {
      dispatch(removePageFavorite({ page_name: "ITB Importer" }));
    } else {
      dispatch(
        addPageFavorite({ page_name: "ITB Importer", page_url: path })
      );
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    const gmailConnected = searchParams.get("gmail_connected");
    const gmailError = searchParams.get("gmail_error");

    if (gmailConnected === "true") {
      dispatch(clearITBMessages());
      dispatch(setITBSuccessMessage("Gmail connected successfully!"));
      console.log("✓ OAuth callback received - Gmail connected");
      
      // Check status after OAuth
      setTimeout(() => {
        dispatch(getGmailConnectionStatus());
      }, 1000);
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (gmailError) {
      dispatch(setITBErrorMessage(`Gmail connection failed: ${gmailError}`));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams, dispatch]);

  // Initial load
  useEffect(() => {
    dispatch(getGmailConnectionStatus());
    dispatch(fetchPageFavorite("ITB Importer"));
    // Check if there's an active scan or import in progress
    dispatch(checkScanStatus());
    dispatch(checkImportStatus());
  }, [dispatch]);

  // Load ITBs when status changes or filter changes
  useEffect(() => {
    if (gmailStatus.is_connected) {
      dispatch(listStagedITBs(filter));
    }
  }, [gmailStatus.is_connected, filter, dispatch]);

  // Auto-clear messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => dispatch(clearITBMessages()), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => dispatch(clearITBMessages()), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, dispatch]);

  useEffect(() => {
    if (importStatus && importStatus.status === 'completed' && importStatus.imported_count > 0) {
      const timer = setTimeout(() => {
        navigate("/myganarpro/favorites");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [importStatus, navigate]);

  const renderScanProgress = () => {
    if (!scanStatus || scanStatus.status !== "processing") return null;

    const percent = scanStatus.progress ?? 0;
    const totalEmails = scanStatus.total || 0;
    const totalItbs = scanStatus.itbs_found || 0;
    const baseTotal = totalItbs || totalEmails;
    const message = scanStatus.message || "Processing ITB emails...";
    const processedCount =
      baseTotal > 0 ? Math.round((percent / 100) * baseTotal) : 0;
    const showCounts = baseTotal > 0;

    return (
      <div
        className="mb-4 p-4 rounded"
        style={{ backgroundColor: "#e8f4ff" }}
      >
        <div className="fw-bold text-primary mb-3">
          {message}
          {/* {!message.includes("Filtering emails") && ` ${processedCount}/${baseTotal}`} */}
        </div>
        <div className="progress" style={{ height: "10px" }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${percent}%` }}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <div className="mt-3 text-muted">
          {percent}% complete
          {totalEmails > 0 && ` • ${totalEmails} emails`}
          {totalItbs > 0 && ` • ${totalItbs} ITBs found`}
        </div>
        <div className="mt-2 text-muted fst-italic">
          Only emails from the last 15 days are being scanned.
        </div>
      </div>
    );
  };

  const renderImportProgress = () => {
    if (!importStatus || (importStatus.status !== "processing" && importStatus.status !== "queued")) return null;

    const percent = importStatus.progress ?? 0;
    const total = importStatus.total || 0;
    const importedCount = importStatus.imported_count || 0;
    const message = importStatus.message || "Importing deals...";

    return (
      <div
        className="mb-4 p-4 rounded"
        style={{ backgroundColor: "#e8f5e9" }}
      >
        <div className="fw-bold text-success mb-3">
          {message}
        </div>
        <div className="progress" style={{ height: "10px" }}>
          <div
            className="progress-bar bg-success"
            role="progressbar"
            style={{ width: `${percent}%` }}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <div className="mt-3 text-muted">
          {percent}% complete
          {total > 0 && ` • ${importedCount}/${total} deals imported`}
        </div>
        {importStatus.errors && importStatus.errors.length > 0 && (
          <div className="mt-2 text-warning">
            <small>
              {importStatus.errors.length} error(s) encountered during import
            </small>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="itb-import-page">
      <div className="container py-5">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="d-flex flex-column">
                  <Row>
                    <h1 className="col display-6 text-start">Import ITBs from Gmail</h1>
                    {user && (
                      <Col sm={1}>
                        <FontAwesomeIcon
                          size={"2x"}
                          icon={isFavorite ? solidStar : regularStar}
                          style={{
                            color: isFavorite ? "gold" : "transparent",
                            stroke: isFavorite ? "" : "gold",
                            strokeWidth: isFavorite ? "0" : "20",
                            cursor: "pointer",
                          }}
                          onClick={handleFavoriteToggle}
                          className="fw-bolder"
                        />
                      </Col>
                    )}
                  </Row>
                </div>
                <p className="text-muted">
                  Automatically import Invitation to Bid emails from your Gmail inbox and convert them to deals
                </p>
              </div>
              <Link to="/myganarpro/favorites" className="btn btn-outline-primary">
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                Back to Pipeline
              </Link>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <Alert variant="success" dismissible onClose={() => dispatch(clearITBMessages())}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert variant="danger" dismissible onClose={() => dispatch(clearITBMessages())}>
            {errorMessage}
          </Alert>
        )}

        {/* Scan Progress */}
        {renderScanProgress()}
        {renderImportProgress()}

        {/* Gmail Connection Section */}
        <Card className="mb-4 shadow-sm border-0">
          <Card.Body className="p-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              {/* Left side - Status Info */}
              <div className="text-start flex-grow-1 mb-3 mb-md-0">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                    <FontAwesomeIcon icon={faEnvelope} className="text-primary fs-4" />
                  </div>
                  <div>
                    <h4 className="mb-1 fw-bold">Gmail Connection</h4>
                    <p className="text-muted small mb-0">
                      {gmailStatus.is_connected 
                        ? "Your Gmail account is connected" 
                        : "Connect your Gmail to import ITB emails automatically"}
                    </p>
                  </div>
                </div>
                
                {loadingStatus ? (
                  <div className="ms-5 ps-4">
                    <BSSpinner animation="border" size="sm" className="me-2" />
                    <span className="text-muted">Checking connection...</span>
                  </div>
                ) : gmailStatus.is_connected ? (
                  <div className="ms-5 ps-4">
                    <div className="d-flex align-items-center mb-2">
                      <Badge bg="success" className="me-2 px-3 py-2">
                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                        Connected
                      </Badge>
                      <span className="text-dark">
                        <strong>{gmailStatus.connected_email}</strong>
                      </span>
                    </div>
                    <p className="text-muted small mb-0">
                      <i className="bi bi-clock me-1"></i>
                      Connected on {new Date(gmailStatus.connected_at).toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <div className="ms-5 ps-4">
                    <Badge bg="secondary" className="px-3 py-2">
                      <FontAwesomeIcon icon={faTimes} className="me-1" />
                      Not Connected
                    </Badge>
                  </div>
                )}
              </div>
              
              {/* Right side - Action Buttons */}
              <div className="text-end d-flex flex-column flex-md-row gap-2 align-items-md-center">
                {gmailStatus.is_connected ? (
                  <>
                    <Button 
                      variant="primary" 
                      onClick={handleScanInbox}
                      disabled={scanning || (!hasFeatureAccess && (!free_itbs || free_itbs <= 0))}
                      className="d-flex align-items-center justify-content-center"
                      style={{ minWidth: '140px' }}
                    >
                      {scanning ? (
                        <>
                          <BSSpinner animation="border" size="sm" className="me-2" />
                          {scanStatus?.progress ? `${scanStatus.progress}%` : 'Scanning...'}
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faSync} className="me-2" />
                          {(!hasFeatureAccess && (!free_itbs || free_itbs <= 0))
                            ? "No Imports Left"
                            : "Scan Inbox"}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => dispatch(getGmailConnectionStatus())}
                      disabled={loadingStatus}
                      size="sm"
                      className="d-flex align-items-center justify-content-center"
                    >
                      <FontAwesomeIcon icon={faSync} className="me-1" />
                      {loadingStatus ? "Refreshing..." : "Refresh"}
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={handleDisconnectGmail}
                      size="sm"
                      className="d-flex align-items-center justify-content-center"
                    >
                      <i className="bi bi-plug me-1"></i>
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="primary" 
                      onClick={handleConnectGmail}
                      className="d-flex align-items-center justify-content-center"
                      style={{ minWidth: '160px' }}
                    >
                      <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                      Connect Gmail
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => dispatch(getGmailConnectionStatus())}
                      disabled={loadingStatus}
                      size="sm"
                      className="d-flex align-items-center justify-content-center"
                    >
                      <FontAwesomeIcon icon={faSync} className="me-1" />
                      {loadingStatus ? "Checking..." : "Check Status"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* ITBs List */}
        {gmailStatus.is_connected && (
          <>
            {/* Filter Tabs */}
            <div className="mb-3">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${filter === "pending" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handleFilterChange("pending")}
                >
                  Pending ({counts.pending})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === "imported" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handleFilterChange("imported")}
                >
                  Imported ({counts.imported})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === "rejected" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handleFilterChange("rejected")}
                >
                  Rejected ({counts.rejected})
                </button>
              </div>
            </div>

            {/* Action Bar */}
            {filter === "pending" && stagedITBs.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-3">
                  <Form.Check
                    type="checkbox"
                    label={`Select All (${selectedITBs.length}/${stagedITBs.filter(itb => itb.status === 'pending').length})`}
                    checked={
                      selectedITBs.length ===
                        stagedITBs.filter(itb => itb.status === "pending").length &&
                      stagedITBs.filter(itb => itb.status === "pending").length > 0
                    }
                    onChange={handleSelectAll}
                  />
                  {!hasFeatureAccess && (
                    <span className="text-muted small">
                      Free ITB imports remaining:{" "}
                      <Badge bg={free_itbs > 0 ? "success" : "secondary"}>
                        {free_itbs ?? 0}
                      </Badge>
                    </span>
                  )}
                  {hasFeatureAccess && (
                    <span className="text-muted small">
                      ITB Import is included in your plan (unlimited imports).
                    </span>
                  )}
                </div>
                <Button
                  variant="success"
                  disabled={selectedITBs.length === 0 || importing || scanning}
                  onClick={handleOpenImportModal}
                >
                  {importing ? (
                    <>
                      <BSSpinner animation="border" size="sm" className="me-2" />
                      Importing...
                    </>
                  ) : (
                    `Import Selected (${selectedITBs.length})`
                  )}
                </Button>
              </div>
            )}

            {/* ITBs Cards */}
            {loadingITBs ? (
              <div className="text-center py-5">
                <Spinner />
              </div>
            ) : stagedITBs.length === 0 ? (
              <Card>
                <Card.Body className="text-center py-5">
                  <p className="text-muted mb-0">
                    {filter === "pending" 
                      ? "No pending ITBs found. Click 'Scan Inbox' to find ITBs in your email."
                      : `No ${filter} ITBs found.`}
                  </p>
                </Card.Body>
              </Card>
            ) : (
              <div className="row">
                {stagedITBs.map((itb) => (
                  <div key={itb.id} className="col-md-6 mb-3">
                    <Card className={`h-100 ${selectedITBs.includes(itb.id) ? "border-primary" : ""}`}>
                      <Card.Body className="text-start">
                        {filter === "pending" && (
                          <Form.Check
                            type="checkbox"
                            className="float-end"
                            checked={selectedITBs.includes(itb.id)}
                            onChange={() => handleSelectITB(itb.id)}
                          />
                        )}
                        <h5 className="card-title">{itb.project_name || "Untitled Project"}</h5>
                        <h6 className="card-subtitle mb-2 text-muted">{itb.gc_name || "Unknown GC"}</h6>
                        
                        <div className="mb-2">
                          <small className="text-muted">From:</small> {itb.email_from}
                        </div>
                        
                        {itb.bid_due_date && (
                          <div className="mb-2">
                            <small className="text-muted">Due Date:</small>{" "}
                            <Badge bg="warning" text="dark">
                              {new Date(itb.bid_due_date).toLocaleDateString()}
                            </Badge>
                          </div>
                        )}
                        
                        {itb.project_location && (
                          <div className="mb-2">
                            <small className="text-muted">Location:</small> {itb.project_location}
                          </div>
                        )}
                        
                        <div className="mb-3">
                          <small className="text-muted">Subject:</small>
                          <p className="small mb-0 text-truncate">{itb.email_subject}</p>
                        </div>
                        
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            {/*<Badge bg="info" className="me-1">
                              Confidence: {(itb.confidence_score * 100).toFixed(0)}%
                            </Badge>*/}
                            {itb.attachments && itb.attachments.length > 0 && (
                              <Badge bg="secondary">
                                {itb.attachments.length} attachment(s)
                              </Badge>
                            )}
                          </div>
                          {filter === "pending" && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRejectITB(itb.id)}
                            >
                              Reject
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Import Confirmation Modal */}
        <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Import</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to import {selectedITBs.length} ITB(s) as deals?
            They will be added to your pipeline.
            {!hasFeatureAccess && (
              <p className="mt-3 mb-0 text-muted small">
                This will use your free ITB imports. Remaining after import:{" "}
                <strong>{Math.max((free_itbs || 0) - selectedITBs.length, 0)}</strong>
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleImportITBs}>
              Import Deals
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default ITBImport;

